import React, { useCallback, useMemo, useState } from 'react';
import { get, isEqual } from 'lodash';

import {
	classNames,
	useClassNames,
} from '@common';

import {
	IconButton,
} from '@components';

import {
	SelectArrowIcon,
} from '@icons';

import './Select.scss';

interface SelectProps<T> {
	onChange?: (value: T) => void,
	// 	textName?: string | keyof T,
	textName?: string,
	options?: T[],
	value: T,
	name?: string,

	trailing?: JSX.Element,
	leading?: JSX.Element,
	className?: string,
	disabled?: boolean,
	allowSelectText?: boolean,
}

function Select<T>(props: SelectProps<T>): JSX.Element {
	const {
		className,
		trailing,
		disabled,
		textName,
		onChange,
		leading,
		options,
		value,
		name,
		allowSelectText,
	} = props;

	const [showOptions, setShowOptions] = useState<boolean>(false);

	const handleToggleShowOptions = useCallback((): void => {
		setShowOptions(!showOptions);
	}, [setShowOptions, showOptions]);

	const handleChange = useCallback((option: T) => () => {
		onChange(option);
		setShowOptions(false);
	}, [onChange, setShowOptions]);

	const optionsView = useMemo((): JSX.Element => {
		return showOptions && options && (
			<div className="select__options">
				{options.map((option, i) => {
					const
						isSelected = isEqual(option, value),
						classes = classNames(
							'select__option',
							isSelected && 'selected',
						);

					return (
						<div
							onClick={handleChange(option)}
							className={classes}
							key={i}
						>
							{textName ? get(option, textName) : option}
						</div>
					);
				})}
			</div>
		);
	}, [options, value, handleChange, showOptions, textName]);

	const trailingView = useMemo((): JSX.Element => {
		return trailing || (
			<IconButton
				icon={<SelectArrowIcon />}
				iconSize="20px"
				size="nogap"
			/>
		);
	}, [trailing]);

	const classes = useClassNames(
		'select',
		!allowSelectText && 'text-clickable',
		className,
		disabled && 'disabled',
	);

	return (
		<div className={classes} id={name} onClick={() => !allowSelectText ? handleToggleShowOptions() : null}>
			<div className="select__leading">
				{leading}
			</div>
			<div className="select__value">
				{textName ? get(value, textName) : value}
				{optionsView}
			</div>
			<div
				className="select__trailing"
				onClick={handleToggleShowOptions}
			>
				{trailingView}
			</div>
		</div>
	);
}

export {Select};